import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { userQueryOptions } from '@/shared/lib/tanstack'
import { useLanguage } from '@/shared/lib/i18n'
import { SERVER_LANGUAGE_MAP } from '@/shared/constants/language'
import { useAuth } from '@/shared/hooks/use-auth'
import { useUpdateUserPreferences } from '@/shared/hooks/use-update-user-preferences'
import type { User } from '@/shared/types/global.types'

export function useUser() {
  const { isAuthenticated: enabled, user: sessionUser } = useAuth()
  const { setLanguage } = useLanguage()
  const { mutate: updatePreferences } = useUpdateUserPreferences()

  const { data: user, ...query } = useQuery({ ...userQueryOptions, enabled })

  // When the Kanguro user API data isn't cached yet (e.g. first offline load),
  // fall back to the session user. The session offline fallback reads USER_PROFILE_KEY
  // from secure storage, which stores the full User object (fullName, ciUserId, etc.)
  const effectiveUser = user ?? (sessionUser as User | undefined)

  const userLanguage = effectiveUser?.language

  useEffect(() => {
    if (!userLanguage) return
    const lang = SERVER_LANGUAGE_MAP[userLanguage]
    setLanguage(lang)
  }, [userLanguage])

  return { user: effectiveUser, ...query, updatePreferences }
}
